import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn, Phone, Globe, MessageCircle, Calculator, FileText } from "lucide-react";
import { brandPhoneLink, whatsappLink } from "../lib/whatsapp";

/**
 * StickyUtilityBar — slim utility bar that drops down from the top after
 * the user scrolls past the hero. Hosts quick-access actions:
 *   • Iniciar sesión / Mi portal
 *   • Llámanos
 *   • WhatsApp
 *   • Calculadora (jump anchor)
 *   • Idioma ES/EN
 *   • Planes
 *
 * Hides on /admin/* and /portal/* routes.
 */
export default function StickyUtilityBar() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 720);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) return null;

  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const toggleLang = () => i18n.changeLanguage(lang === "es" ? "en" : "es");

  const labels = {
    es: {
      signin: "Iniciar sesión",
      plans: "Planes",
      calc: "Calculadora",
      wa: "WhatsApp",
    },
    en: {
      signin: "Sign in",
      plans: "Plans",
      calc: "Calculator",
      wa: "WhatsApp",
    },
  }[lang];

  return (
    <div
      className={`hidden md:block fixed top-0 inset-x-0 z-[45] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        show ? "translate-y-0" : "-translate-y-full"
      }`}
      aria-hidden={!show}
      data-testid="sticky-utility-bar"
    >
      <div className="bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
        <div className="max-w-[1180px] mx-auto px-4 py-2 flex items-center gap-2">
          {/* Brand mini */}
          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-1.5 mr-1 shrink-0"
            data-testid="sticky-brand"
          >
            <img src="/velcor-logo.png" alt="" className="w-6 h-6 object-contain" />
            <span className="font-black text-velcor-ink text-[12.5px] tracking-tight">Velcor</span>
          </Link>

          <span className="hidden md:block w-px h-5 bg-slate-200 mx-1" aria-hidden />

          {/* Quick links — center scrollable on mobile */}
          <nav
            className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar"
            aria-label="Acceso rápido"
          >
            <PillLink to="/residential" icon={FileText} label={labels.plans} testid="sticky-link-plans" />
            <PillExternal
              href={whatsappLink("Hola Velcor, quiero información sobre los planes.")}
              icon={MessageCircle}
              label={labels.wa}
              accent="green"
              testid="sticky-link-wa"
            />
            <PillExternal
              href={brandPhoneLink()}
              icon={Phone}
              label="(800) 715-6516"
              testid="sticky-link-phone"
            />
          </nav>

          <span className="hidden md:block w-px h-5 bg-slate-200 mx-1" aria-hidden />

          {/* Lang toggle */}
          <button
            onClick={toggleLang}
            className="inline-flex items-center gap-1 rounded-full px-2.5 h-8 text-[11.5px] font-extrabold text-slate-600 hover:text-velcor-ink hover:bg-slate-100 transition-colors uppercase tracking-[0.08em] shrink-0"
            data-testid="sticky-lang"
            aria-label="Cambiar idioma"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "es" ? "EN" : "ES"}
          </button>

          {/* Sign in CTA — primary */}
          <Link
            to="/portal/login"
            className="inline-flex items-center gap-1.5 rounded-full bg-velcor-ink text-white hover:bg-velcor-ink/90 px-3.5 h-8 text-[12px] font-extrabold transition-colors shrink-0"
            data-testid="sticky-signin"
          >
            <LogIn className="w-3.5 h-3.5" strokeWidth={2.4} />
            {labels.signin}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PillLink({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[12px] font-bold text-slate-600 hover:text-velcor-ink hover:bg-slate-100 transition-colors shrink-0"
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </Link>
  );
}

function PillAnchor({ href, icon: Icon, label, testid }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[12px] font-bold text-slate-600 hover:text-velcor-ink hover:bg-slate-100 transition-colors shrink-0"
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </a>
  );
}

function PillExternal({ href, icon: Icon, label, accent, testid }) {
  const accentClass =
    accent === "green"
      ? "text-velcor-green hover:bg-velcor-green/10"
      : "text-slate-600 hover:text-velcor-ink hover:bg-slate-100";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[12px] font-bold transition-colors shrink-0 ${accentClass}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </a>
  );
}
